import { FRAME_COMPONENT_CSS } from "./html-frame-components-style";

/**
 * Base stylesheet injected into every HTML frame iframe: the theme tokens
 * (dark here, light in {@link LIGHT_OVERRIDE}), element defaults and the
 * frame design system's components. Rules colour only through tokens, so
 * the theme fix scripts below never rewrite them.
 */
export const BASE_STYLE = `<style>
/* Reset & base */
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{color-scheme:dark;--accent:#3AF88C;--on-accent:#000000;--bg:#1C1C22;--surface:#26262E;--surface-raised:#31313A;--selected:#3C3C46;--border:rgba(255,255,255,0.08);--hover:rgba(255,255,255,0.03);--text:#F5F5F7;--text-secondary:rgba(255,255,255,0.72);--text-muted:rgba(255,255,255,0.56);--positive:var(--accent);--warning:#E2B45F;--negative:#F28B82;--info:#8AB4F8;--positive-soft:color-mix(in srgb,var(--positive) 16%,var(--surface));--warning-soft:color-mix(in srgb,var(--warning) 16%,var(--surface));--negative-soft:color-mix(in srgb,var(--negative) 16%,var(--surface));--info-soft:color-mix(in srgb,var(--info) 16%,var(--surface));--danger:var(--negative);--blue:var(--info);--orange:var(--warning);--purple:#C4A7F5;--chart-1:var(--accent);--chart-2:#8AB4F8;--chart-3:#C4A7F5;--chart-neutral:rgba(255,255,255,0.24);--track:rgba(255,255,255,0.06);--shadow:none;--fs-xs:11px;--fs-sm:12px;--fs-base:13px;--fs-md:15px;--fs-xl:22px;--fs-2xl:28px;--space-1:4px;--space-2:8px;--space-3:12px;--space-4:16px;--space-5:24px;--space-6:32px;--space-7:48px;--radius:10px;font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display","Segoe UI",Roboto,Helvetica,Arial,sans-serif}
html{height:100%;scrollbar-gutter:stable}
body{height:100%}
body{background:var(--bg);color:var(--text);font-size:var(--fs-base);line-height:1.6;padding:16px;-webkit-font-smoothing:antialiased;overflow:auto}
:is([data-tab-panel],[data-tags],[data-filter-empty])[hidden]{display:none!important}

/* Typography */
h1{font-size:22px;font-weight:700;letter-spacing:-0.3px;margin-bottom:12px;color:var(--text)}
h2{font-size:17px;font-weight:600;letter-spacing:-0.2px;margin:20px 0 8px;color:var(--text)}
h3{font-size:14px;font-weight:600;margin:16px 0 6px;color:var(--text)}
h4{font-size:13px;font-weight:600;margin:12px 0 4px;color:var(--text-secondary)}
p{margin:0 0 10px;color:var(--text-secondary)}
strong{color:var(--text);font-weight:600}
small{font-size:11px;color:var(--text-muted)}

/* Links */
a{color:var(--accent);text-decoration:none}
a:hover{text-decoration:underline}

/* Lists */
ul,ol{margin:0 0 12px 20px;color:var(--text-secondary)}
li{margin-bottom:4px}
li::marker{color:var(--text-muted)}

/* Tables */
table{width:100%;border-collapse:collapse;margin:12px 0;font-size:var(--fs-sm)}
th{text-align:left;padding:8px 12px;color:var(--text-muted);font-weight:600;border-bottom:1px solid var(--border)}
td{padding:8px 12px;border-bottom:1px solid var(--border);color:var(--text-secondary);transition:background-color var(--duration-quick,150ms) ease-out}
tr:last-child td{border-bottom:none}
tr:hover td{background:var(--hover)}
@media(prefers-reduced-motion:reduce){td{transition:none}}

/* Code */
code{font-family:"SF Mono",Monaco,Menlo,"Courier New",monospace;font-size:12px;background:var(--surface);padding:2px 5px;border-radius:4px;color:var(--text)}
pre{background:var(--surface);padding:12px 16px;border-radius:8px;overflow-x:auto;margin:12px 0;border:1px solid var(--border)}
pre code{background:none;padding:0;font-size:11px;color:var(--text)}

/* Grid & flex helpers */
.grid{display:grid;gap:12px}.grid-2{grid-template-columns:repeat(2,1fr)}.grid-3{grid-template-columns:repeat(3,1fr)}
.flex{display:flex;gap:8px}.flex-col{flex-direction:column}.flex-between{justify-content:space-between;align-items:center}

/* Divider */
hr{border:none;border-top:1px solid var(--border);margin:16px 0}

/* SVG charts */
svg text{fill:var(--text-secondary);font-family:inherit;font-size:11px}
svg line,svg path.grid-line{stroke:var(--border)}
${FRAME_COMPONENT_CSS}
/* Opt-in reading layout for a composed brief; other HTML keeps its layout. */
.brief{max-width:880px;margin:0 auto;overflow-wrap:anywhere}
.brief header{margin-bottom:16px}
.brief header small{display:block;margin-bottom:6px}
.brief h1{font-size:clamp(23px,4vw,30px);line-height:1.2;letter-spacing:-0.6px;margin:0 0 10px}
.brief .brief-lead{font-size:15px;line-height:1.5;margin:0;max-width:68ch}
.brief-points{list-style:none;margin:0;padding:0}
.brief-points>li{margin:0;padding:12px 0;border-top:1px solid var(--border)}
.brief-points>li h2{font-size:14px;line-height:1.4;margin:0 0 4px}
.brief-points>li p{margin:0;line-height:1.55}
.brief .brief-sources{display:flex;flex-wrap:wrap;align-items:baseline;gap:4px 14px;margin-top:16px;padding-top:10px;border-top:1px solid var(--border);font-size:11px;color:var(--text-muted)}
.brief .brief-sources p{font-size:inherit;margin:0}
.brief figure{min-width:0;margin:16px 0}
.brief img{display:block;max-width:100%;height:auto;border-radius:8px}
.brief figure img{width:100%;max-height:360px;object-fit:contain;object-position:left top}
.brief figcaption{margin-top:6px;font-size:11px;line-height:1.5;color:var(--text-muted)}
.brief details{margin-top:18px;padding-top:12px;border-top:1px solid var(--border)}
.brief summary{cursor:pointer;font-weight:600;color:var(--text)}
.brief summary:focus-visible{outline:2px solid var(--accent);outline-offset:4px;border-radius:2px}
.brief details[open]>summary{margin-bottom:10px}
.brief .grid-2{grid-template-columns:repeat(2,minmax(0,1fr))}
.brief .grid-3{grid-template-columns:repeat(3,minmax(0,1fr))}
.brief .grid>*{min-width:0}
.brief-hero{display:grid;grid-template-columns:minmax(0,1.2fr) minmax(0,1fr);align-items:center;gap:24px;margin-bottom:20px}
.brief-hero>header,.brief .brief-hero>.brief-media{margin:0}
.brief .brief-hero>.brief-media img{max-height:240px;object-fit:contain}
.brief-stat{display:flex;flex-direction:column;gap:4px;padding:12px 0;border-top:1px solid var(--border)}
.brief-stat>strong{font-size:clamp(26px,4vw,36px);line-height:1.1;letter-spacing:-0.6px}
.brief-stat>span{font-size:11px;line-height:1.5;color:var(--text-muted)}
.brief-stat small{display:block;margin-top:2px}
.brief .brief-source{display:grid;grid-template-columns:64px minmax(0,1fr);align-items:center;gap:10px;padding:10px 0;font-size:12px;line-height:1.45;color:var(--text-secondary);text-decoration:none}
.brief .brief-source>img{width:64px;height:48px;object-fit:cover;border-radius:6px}
.brief-source>span{min-width:0}
.brief-source strong{display:block;font-size:12px;line-height:1.4}
.brief-source small{display:block;margin-top:2px}
.brief .brief-source:hover{ text-decoration:none }
.brief-source:hover strong{text-decoration:underline;text-decoration-color:var(--accent);text-underline-offset:3px}
.brief-source:focus-visible{outline:2px solid var(--accent);outline-offset:4px;border-radius:6px}
@media(max-width:560px){.brief .grid-2,.brief .grid-3,.brief .brief-hero{grid-template-columns:minmax(0,1fr)}.brief-hero{gap:16px}}
/* Report card, every project's report: one ring (--value of --max) captioned with what it counts, three numbers, three next steps. */
.report{max-width:560px;display:flex;flex-direction:column;gap:var(--space-4);overflow-wrap:anywhere}
.report-head{display:grid;grid-template-columns:auto minmax(0,1fr);align-items:center;gap:18px}
.report-head h1{font-size:18px;font-weight:650;line-height:1.25;letter-spacing:-0.3px;margin:0 0 4px}
.report-head p{margin:0;line-height:1.5}
.report-ring{position:relative;display:flex;flex-direction:column;align-items:center;gap:6px;width:96px}
.report-ring::before{content:"";position:absolute;top:0;left:0;width:100%;aspect-ratio:1;border-radius:50%;background:conic-gradient(var(--accent) calc(min(var(--value,0) / max(var(--max,1),1),1) * 1turn),var(--track) 0);-webkit-mask:radial-gradient(farthest-side,transparent calc(100% - 10px),black calc(100% - 9.5px));mask:radial-gradient(farthest-side,transparent calc(100% - 10px),black calc(100% - 9.5px))}
.report-ring b{position:relative;display:grid;place-items:center;width:100%;aspect-ratio:1;font-size:20px;font-weight:650;letter-spacing:-0.3px;color:var(--text);font-variant-numeric:tabular-nums}
.report-ring span{font-size:var(--fs-xs);line-height:1.3;text-align:center;color:var(--text-muted)}
.report-tiles{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:var(--space-2)}
.report-tile{display:flex;flex-direction:column;gap:2px;min-width:0;padding:10px 12px;border-radius:var(--radius);background:var(--surface)}
.report-tile b{font-size:20px;font-weight:650;line-height:1.2;color:var(--text);font-variant-numeric:tabular-nums}
.report-tile span{font-size:var(--fs-sm);line-height:1.4;color:var(--text-muted)}
.report-next{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:6px}
.report-next li{display:flex;align-items:baseline;gap:8px;margin:0;color:var(--text-secondary)}
.report-next li::before{content:"";flex:none;width:6px;height:6px;border-radius:2px;background:var(--accent)}
@media(max-width:420px){.report-head{gap:var(--space-3)}.report-ring{width:80px}.report-ring b{font-size:17px}.report-tiles{grid-template-columns:minmax(0,1fr)}}
</style>`;

export const LIGHT_OVERRIDE = `<style>
:root{color-scheme:light;--bg:#FFFFFF;--surface:#F4F4F6;--surface-raised:#E9E9EE;--selected:#FFFFFF;--text:#1D1D1F;--text-secondary:#3C3C43;--text-muted:#636369;--border:rgba(0,0,0,0.1);--hover:rgba(0,0,0,0.03);--accent:#248A3D;--on-accent:#000000;--positive:color-mix(in srgb,var(--accent) 72%,#000000);--warning:#7A4F00;--negative:#B3261E;--info:#1650B0;--purple:#6E4BB8;--chart-2:#4F6FA8;--chart-3:#8667C7;--chart-neutral:rgba(0,0,0,0.2);--track:rgba(0,0,0,0.05);--shadow:0 1px 2px rgba(0,0,0,0.1)}
body{background:var(--bg)!important;color:var(--text)!important}
h1,h2,h3{color:var(--text)!important}
h4{color:var(--text-secondary)!important}
p{color:var(--text-secondary)!important}
strong{color:var(--text)!important}
small{color:var(--text-muted)!important}
ul,ol{color:var(--text-secondary)!important}
li::marker{color:var(--text-muted)!important}
th{color:var(--text-muted)!important}
td{color:var(--text-secondary)!important}
code{color:var(--text)!important}
pre{background:var(--surface)!important;border-color:var(--border)!important}
pre code{color:var(--text)!important}
svg text{fill:var(--text-secondary)!important}
.card,.section{background:var(--surface)!important}
.metric{color:var(--text)!important}
.metric-label{color:var(--text-muted)!important}
tr:hover td{background:var(--hover)!important}
hr{border-color:var(--border)!important}
a{color:var(--accent)!important}
a.btn{color:var(--text)!important}
.report-tile{background:var(--surface)!important}
.report-ring b,.report-tile b{color:var(--text)!important}
.report-ring span,.report-tile span{color:var(--text-muted)!important}
.report-next li{color:var(--text-secondary)!important}
</style>`;

/**
 * Script injected into HTML frame iframes in light mode to fix
 * hardcoded dark-theme colors in inline styles.
 */
export const LIGHT_FIX_SCRIPT = `<script>
(function(){
  function resolveColor(c){
    if(!c||!document.body)return null;
    var probe=document.createElement("span");
    probe.style.color=c;
    document.body.appendChild(probe);
    var computed=getComputedStyle(probe).color;
    document.body.removeChild(probe);
    var m=computed.match(/^rgba?\\(\\s*(\\d+)\\s*,\\s*(\\d+)\\s*,\\s*(\\d+)/);
    if(!m)return null;
    return[+m[1],+m[2],+m[3]];
  }
  function isToken(v){var m=typeof v==="string"&&v.match(/var\\(\\s*(--[\\w-]+)/);return !!m&&getComputedStyle(document.documentElement).getPropertyValue(m[1]).trim()!=="";}
  function isTransparent(v){return typeof v==="string"&&(v==="transparent"||v.replace(/\\s/g,"").indexOf("rgba(0,0,0,0)")!==-1);}
  function luminance(rgb){
    var r=rgb[0]/255,g=rgb[1]/255,b=rgb[2]/255;
    r=r<=0.03928?r/12.92:Math.pow((r+0.055)/1.055,2.4);
    g=g<=0.03928?g/12.92:Math.pow((g+0.055)/1.055,2.4);
    b=b<=0.03928?b/12.92:Math.pow((b+0.055)/1.055,2.4);
    return 0.2126*r+0.7152*g+0.0722*b;
  }
  function contrast(a,b){
    var l1=luminance(a),l2=luminance(b);
    var hi=Math.max(l1,l2),lo=Math.min(l1,l2);
    return(hi+0.05)/(lo+0.05);
  }
  function nearestBg(el){
    var cur=el;
    while(cur&&cur!==document.documentElement){
      var bg=resolveColor(getComputedStyle(cur).backgroundColor);
      if(bg&&!isTransparent(getComputedStyle(cur).backgroundColor))return bg;
      cur=cur.parentElement;
    }
    var bodyRaw=getComputedStyle(document.body).backgroundColor;
    return isTransparent(bodyRaw)?[255,255,255]:(resolveColor(bodyRaw)||[255,255,255]);
  }
  function fix(){
    var els=document.querySelectorAll("[style]");
    for(var i=0;i<els.length;i++){
      var s=els[i].style;
      if(s.color&&!isToken(s.color)){var rgb=resolveColor(s.color);if(rgb&&luminance(rgb)>0.6)s.color="var(--text)";}
      if(s.backgroundColor&&!isToken(s.backgroundColor)&&!isTransparent(s.backgroundColor)){var bg=resolveColor(s.backgroundColor);if(bg&&luminance(bg)<0.25)s.backgroundColor="var(--surface)";}
    }
    var styles=document.querySelectorAll("style");
    for(var j=0;j<styles.length;j++){
      var sheet=styles[j].sheet;if(!sheet)continue;
      try{
        for(var k=0;k<sheet.cssRules.length;k++){
          var rule=sheet.cssRules[k];if(!rule.style)continue;
          if(rule.style.color&&!isToken(rule.style.color)){var rc=resolveColor(rule.style.color);if(rc&&luminance(rc)>0.6)rule.style.color="var(--text)";}
          if(rule.style.backgroundColor&&!isToken(rule.style.backgroundColor)&&!isTransparent(rule.style.backgroundColor)){var rb=resolveColor(rule.style.backgroundColor);if(rb&&luminance(rb)<0.25)rule.style.backgroundColor="var(--surface)";}
        }
      }catch(e){}
    }
    var all=document.body?document.body.querySelectorAll("*"):[];
    for(var m=0;m<all.length;m++){
      var el=all[m],cs=getComputedStyle(el);
      if(isToken(el.style.color))continue;
      var fg=resolveColor(cs.color),bgc=nearestBg(el);
      if(fg&&bgc&&contrast(fg,bgc)<4.5)el.style.color="var(--text)";
    }
  }
  var moRef=null;
  function watch(){
    moRef=new MutationObserver(function(){
      if(!moRef)return;
      moRef.disconnect();
      fix();
      Promise.resolve().then(function(){
        if(moRef)moRef.observe(document.documentElement,{subtree:true,attributes:true,attributeFilter:["style","class"],childList:true});
      });
    });
    moRef.observe(document.documentElement,{subtree:true,attributes:true,attributeFilter:["style","class"],childList:true});
  }
  if(document.readyState==="loading")document.addEventListener("DOMContentLoaded",function(){fix();watch();});
  else {fix();watch();}
})();
</scr${""}ipt>`;

/**
 * Dark-mode mirror of LIGHT_FIX_SCRIPT. Remaps agent-supplied colors
 * that fail contrast in dark mode (dark text on dark bg, light bg
 * blocks). Uses the same resolveColor() helper.
 */
export const DARK_FIX_SCRIPT = `<script>
(function(){
  function resolveColor(c){
    if(!c||!document.body)return null;
    var probe=document.createElement("span");
    probe.style.color=c;
    document.body.appendChild(probe);
    var computed=getComputedStyle(probe).color;
    document.body.removeChild(probe);
    var m=computed.match(/^rgba?\\(\\s*(\\d+)\\s*,\\s*(\\d+)\\s*,\\s*(\\d+)/);
    if(!m)return null;
    return[+m[1],+m[2],+m[3]];
  }
  // Token-already-applied guard: prevents the MutationObserver from
  // re-firing fix() in an infinite loop when our own writes change a
  // style attribute. If the value already references a defined CSS var,
  // skip; an undefined one (var(--text-primary,#222)) uses its fallback.
  function isToken(v){var m=typeof v==="string"&&v.match(/var\\(\\s*(--[\\w-]+)/);return !!m&&getComputedStyle(document.documentElement).getPropertyValue(m[1]).trim()!=="";}
  function isTransparent(v){return typeof v==="string"&&(v==="transparent"||v.replace(/\\s/g,"").indexOf("rgba(0,0,0,0)")!==-1);}
  function luminance(rgb){
    var r=rgb[0]/255,g=rgb[1]/255,b=rgb[2]/255;
    r=r<=0.03928?r/12.92:Math.pow((r+0.055)/1.055,2.4);
    g=g<=0.03928?g/12.92:Math.pow((g+0.055)/1.055,2.4);
    b=b<=0.03928?b/12.92:Math.pow((b+0.055)/1.055,2.4);
    return 0.2126*r+0.7152*g+0.0722*b;
  }
  function contrast(a,b){
    var l1=luminance(a),l2=luminance(b);
    var hi=Math.max(l1,l2),lo=Math.min(l1,l2);
    return(hi+0.05)/(lo+0.05);
  }
  function nearestBg(el){
    var cur=el;
    while(cur&&cur!==document.documentElement){
      var raw=getComputedStyle(cur).backgroundColor;
      var bg=resolveColor(raw);
      if(bg&&!isTransparent(raw))return bg;
      cur=cur.parentElement;
    }
    var bodyRaw=getComputedStyle(document.body).backgroundColor;
    return isTransparent(bodyRaw)?[28,28,34]:(resolveColor(bodyRaw)||[28,28,34]);
  }
  function fix(){
    var els=document.querySelectorAll("[style]");
    for(var i=0;i<els.length;i++){
      var s=els[i].style;
      // Dark mode: dark-on-dark text → use theme text token.
      if(s.color&&!isToken(s.color)){var rgb=resolveColor(s.color);if(rgb&&luminance(rgb)<0.3)s.color="var(--text)";}
      // Light backgrounds → use theme surface token.
      if(s.backgroundColor&&!isToken(s.backgroundColor)&&!isTransparent(s.backgroundColor)){var bg=resolveColor(s.backgroundColor);if(bg&&luminance(bg)>0.7)s.backgroundColor="var(--surface)";}
    }
    var styles=document.querySelectorAll("style");
    for(var j=0;j<styles.length;j++){
      var sheet=styles[j].sheet;if(!sheet)continue;
      try{
        for(var k=0;k<sheet.cssRules.length;k++){
          var rule=sheet.cssRules[k];if(!rule.style)continue;
          if(rule.style.color&&!isToken(rule.style.color)){var rc=resolveColor(rule.style.color);if(rc&&luminance(rc)<0.3)rule.style.color="var(--text)";}
          if(rule.style.backgroundColor&&!isToken(rule.style.backgroundColor)&&!isTransparent(rule.style.backgroundColor)){var rb=resolveColor(rule.style.backgroundColor);if(rb&&luminance(rb)>0.7)rule.style.backgroundColor="var(--surface)";}
        }
      }catch(e){}
    }
    var all=document.body?document.body.querySelectorAll("*"):[];
    for(var m=0;m<all.length;m++){
      var el=all[m],cs=getComputedStyle(el);
      if(isToken(el.style.color))continue;
      var fg=resolveColor(cs.color),bgc=nearestBg(el);
      if(fg&&bgc&&contrast(fg,bgc)<4.5)el.style.color="var(--text)";
    }
  }
  // Disconnect the observer while fix() is running so our own writes
  // don't re-trigger it. Reconnect after the microtask drains.
  var moRef=null;
  function watch(){
    moRef=new MutationObserver(function(){
      if(!moRef)return;
      moRef.disconnect();
      fix();
      // Reconnect on the next microtask, after our writes have settled.
      Promise.resolve().then(function(){
        if(moRef)moRef.observe(document.documentElement,{subtree:true,attributes:true,attributeFilter:["style","class"],childList:true});
      });
    });
    moRef.observe(document.documentElement,{subtree:true,attributes:true,attributeFilter:["style","class"],childList:true});
  }
  if(document.readyState==="loading")document.addEventListener("DOMContentLoaded",function(){fix();watch();});
  else {fix();watch();}
})();
</scr${""}ipt>`;

export const LINK_TARGET_SCRIPT = `<script data-denker-runtime="link-targets">
(function(){
  var SAFE_PROTOCOLS={"http:":true,"https:":true,"mailto:":true,"tel:":true};
  function normalizeHref(href){
    if(!href||href.charAt(0)==="#")return null;
    try{
      var url=new URL(href,document.baseURI);
      return SAFE_PROTOCOLS[url.protocol]?url.href:null;
    }catch(e){
      return null;
    }
  }
  function prepareLink(link){
    var href=normalizeHref(link.getAttribute("href")||"");
    if(!href)return;
    link.setAttribute("target","_blank");
    var rel=(link.getAttribute("rel")||"").split(/\\s+/);
    if(rel.indexOf("noopener")===-1)rel.push("noopener");
    if(rel.indexOf("noreferrer")===-1)rel.push("noreferrer");
    link.setAttribute("rel",rel.filter(Boolean).join(" "));
  }
  function prepare(){
    document.querySelectorAll("a[href]").forEach(prepareLink);
  }
  document.addEventListener("click",function(e){
    var target=e.target&&typeof e.target.closest==="function"?e.target.closest("a[href]"):null;
    if(target)prepareLink(target);
  },true);
  if(document.readyState==="loading")document.addEventListener("DOMContentLoaded",prepare);
  else prepare();
})();
</scr${""}ipt>`;

/**
 * Script injected into HTML frame iframes to bridge text selections
 * back to the parent window via postMessage.
 */
export const SELECTION_BRIDGE_SCRIPT = `<script>
document.addEventListener("mouseup",function(){
  var s=window.getSelection();
  if(!s||s.isCollapsed||!s.rangeCount){
    window.parent.postMessage({type:"denker-text-selection",text:"",rect:null},"*");
    return;
  }
  var t=s.toString().trim();
  if(!t){
    window.parent.postMessage({type:"denker-text-selection",text:"",rect:null},"*");
    return;
  }
  var r=s.getRangeAt(0).getBoundingClientRect();
  window.parent.postMessage({
    type:"denker-text-selection",
    text:t,
    rect:{x:r.x,y:r.y,width:r.width,height:r.height}
  },"*");
});
</script>`;
